import { createMuiTheme } from '@material-ui/core';

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    lightBlueBackground?: Palette['primary'];
  }
  interface PaletteOptions {
    lightBlueBackground?: PaletteOptions['primary'];
  }
}

declare module '@material-ui/core/styles/createMuiTheme' {
  interface Theme {
    sidebarWidth: number;
    sidebarMobileHeight: number;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    sidebarWidth?: number;
    sidebarMobileHeight?: number;
  }
}

export const blue = '#051a5d';
export const darkGrey = '#313232';
export const charcoalGrey = '#464646';
export const disabledGrey = '#7E7F8B';
export const disabledBackground = '#EFEFEFD9';
export const dividerGrey = '#8a8f93';
export const borderGrey = '#D9D9D9';
export const borderDarkGrey = '#707070';
export const buttonText = '#484848';
export const footnoteGrey = '#aeb0b3';
export const mediumGrey = '#3c3c3c';
export const lightGrey = '#bcbec0';
export const offWhite = '#eeeeee';
export const lightPurple = '#8a6ebc';
export const transparentPurple = 'rgba(138, 110, 188, 0.15)';
export const backgroundPurple = '#F0ECF6';
export const purple = '#522F91';
export const white = '#ffffff';
export const transparentWhite = 'rgba(217, 217, 217, 0.25)';
export const transparentYellow = 'rgba(247, 186, 43, 0.12)';
export const transparentGrey = 'rgba(112, 112, 112, 0.12)';
export const yellow = '#f7b92b';
export const transparentYellowBox = '#F7B92B26';
export const yellowOrange = '#f7ba2a';
export const black = '#000000';
export const shadow = '#00000022';
export const red = '#E6919D';
export const green = '#B7D365';
export const poslumaGrey = '#333E47';
export const poslumaBlue = '#43C0E8';
export const poslumaDarkBlue = '#217997';
export const poslumaRed = '#F26F6F';
export const poslumaDarkGrey = '#293238';
export const poslumaBox = '#343C42';

export const universalStyles = {
  grayBox: {
    backgroundColor: charcoalGrey,
    margin: '32px auto 50px',
    padding: '36px 36px 50px',
    maxWidth: 1242,
  },
  poslumaBox: {
    backgroundColor: poslumaBox,
    margin: '32px auto 50px',
    padding: '36px 36px 50px',
    maxWidth: 1242,
  },
  whiteBox: {
    backgroundColor: white,
    padding: 50,
  },
  responsiveVideo: {
    width: '100% !important',
    height: '100% !important',
  },
  videoContainer: {
    width: '100%',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      color: yellow,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
  poslumaList: {
    listStyle: 'none',
    margin: 0,
    marginBottom: 30,
    padding: 0,
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      color: poslumaBlue,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
  orderedList: {
    marginLeft: '1em',
    counterReset: 'li',
    '& > li': {
      counterIncrement: 'li',
      padding: '4px 0',
    },
    '& > li::before': {
      content: 'counter(li) "."',
      marginLeft: '-1em',
    },
    '& > ul > li': {
      padding: '4px 0 4px 20px',
    },
  },
  nestedList: {
    listStyle: 'none',
    margin: 0,
    padding: '0 0 0 30px',
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2013"',
      color: yellow,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
  poslumaNestedList: {
    listStyle: 'none',
    margin: 0,
    padding: '0 0 0 30px',
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2013"',
      color: poslumaBlue,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
};

export const axuminTheme = createMuiTheme({
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  palette: {
    lightBlueBackground: {
      main: '#5BCAEC',
    },
    primary: {
      main: yellow,
      contrastText: darkGrey,
    },
    secondary: {
      main: '#0063b0',
      contrastText: white,
    },
    text: {
      primary: '#2d3436',
      secondary: white,
    },
    action: {
      disabledBackground: lightGrey,
      disabled: darkGrey,
    },
  },
  typography: {
    // white page title
    h1: {
      fontSize: 38,
      color: white,
      marginBottom: 10,
    },
    // 'step' header
    h2: {
      fontSize: 32,
      color: purple,
      fontWeight: 'bold',
      margin: '0 0 30px -50px',
      padding: '0 5px 0 50px',
      display: 'inline-block',
      borderBottom: `2px solid ${yellow}`,
    },
    // black page title
    h3: {
      fontSize: 30,
      color: darkGrey,
    },
    // dark purple page header
    h4: {
      fontSize: 24,
      color: purple,
      fontWeight: 'bold',
      marginBottom: 10,
    },
    // light purple subheader
    h5: {
      fontSize: 22,
      color: lightPurple,
      marginBottom: 30,
      fontWeight: 900,
    },
    // yellow learning check header
    h6: {
      fontSize: 24,
      color: yellow,
      fontWeight: 'bold',
    },
    body1: {
      fontSize: 18,
      color: darkGrey,
    },
    body2: {
      fontSize: 20,
      color: darkGrey,
    },
    caption: {
      fontSize: 16,
      color: darkGrey,
    },
    button: {
      fontSize: 16,
      color: darkGrey,
    },
  },
});

export const poslumaTheme = createMuiTheme({
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  palette: {
    primary: {
      main: poslumaBlue, // button color background
      contrastText: poslumaGrey, // button color text
    },
    secondary: {
      main: '#0063b0',
      contrastText: white,
    },
    text: {
      primary: '#2d3436',
      secondary: white,
    },
    action: {
      disabledBackground: lightGrey,
      disabled: darkGrey,
    },
  },
  typography: {
    // white page title
    h1: {
      fontFamily: 'Martel Sans',
      fontSize: 38,
      color: white,
      marginBottom: 10,
    },
    // 'step' header
    h2: {
      fontSize: 32,
      color: black,
      fontWeight: 'bold',
      margin: '0 0 30px -50px',
      padding: '0 5px 0 50px',
      display: 'inline-block',
      borderBottom: `2px solid ${poslumaBlue}`,
    },
    // darkGrey page title
    h3: {
      fontSize: 30,
      color: darkGrey,
    },
    // dark blue page header
    h4: {
      fontSize: 24,
      color: poslumaDarkBlue,
      fontWeight: 'bold',
      marginBottom: 10,
    },
    h5: {
      fontSize: 22,
      color: poslumaDarkBlue,
      marginBottom: 30,
      fontWeight: 900,
    },
    // blue learning check header
    h6: {
      fontSize: 24,
      color: poslumaBlue,
      fontWeight: 'bold',
    },
    body1: {
      fontSize: 18,
      color: darkGrey,
    },
    body2: {
      fontSize: 20,
      color: poslumaDarkBlue,
    },
    caption: {
      fontSize: 16,
      color: darkGrey,
    },
    button: {
      fontSize: 16,
      color: darkGrey,
    },
  },
});

export default axuminTheme;
